import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { supabase } from '../config/supabase';

const Container = styled.div`
  min-height: calc(100vh - 200px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: #f9fafb;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 40px;
  max-width: 420px;
  width: 100%;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  text-align: center;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  text-align: center;
  margin-bottom: 24px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s;
  outline: none;
  width: 100%;
  padding-right: 45px;

  &:focus {
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const EyeIcon = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;

  &:focus {
    outline: none;
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  background: #5efba9;
  color: #1f2937;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #4de89e;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  border: 1px solid;
  background: ${(props) => (props.$error ? '#fef2f2' : '#ecfdf5')};
  color: ${(props) => (props.$error ? '#dc2626' : '#065f46')};
  border-color: ${(props) => (props.$error ? '#fecaca' : '#a7f3d0')};
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-top: 16px;
  color: #5efba9;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const hasRecoveryTokens = useMemo(() => {
    const hash = window.location.hash || '';
    return hash.includes('access_token=') && hash.includes('type=recovery');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!hasRecoveryTokens) {
      setError('Invalid or expired reset link. Please request a new one.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (updateError) {
      setError(updateError.message || 'Failed to update password.');
      return;
    }

    setSuccess('Password updated successfully. Redirecting to login...');
    setTimeout(() => {
      navigate('/login');
    }, 1200);
  };

  return (
    <Container>
      <Card>
        <Title>Reset Password</Title>
        <Subtitle>Create your new password</Subtitle>

        {error && <Message $error>{error}</Message>}
        {success && <Message>{success}</Message>}

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="new-password">New Password</Label>
            <PasswordInputWrapper>
              <Input
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <EyeIcon type="button" onClick={() => setShowPassword((prev) => !prev)}>
                {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </EyeIcon>
            </PasswordInputWrapper>
          </InputGroup>

          <InputGroup>
            <Label htmlFor="confirm-password">Confirm Password</Label>
            <PasswordInputWrapper>
              <Input
                id="confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <EyeIcon type="button" onClick={() => setShowConfirmPassword((prev) => !prev)}>
                {showConfirmPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </EyeIcon>
            </PasswordInputWrapper>
          </InputGroup>

          <Button type="submit" disabled={loading}>
            {loading ? 'Updating password...' : 'Update Password'}
          </Button>
        </Form>

        <BackLink to="/login">Back to Login</BackLink>
      </Card>
    </Container>
  );
}
